import graphql from "babel-plugin-relay/macro";
import { add, format, parse } from "date-fns";
import { groupBy, isNil, zipWith } from "lodash";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { normalizeBookingRule } from "@whoosh/features/src/Restrictions";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import {
  compareAgendaTimes,
  convertAgendaDateTimeToDate,
} from "@whoosh/utilities/src/dates";

import { useFacilityCourseNoun } from "../../../../Facility/hooks/useFacilityCourseNoun";
import { RestrictionBlockProps } from "../components/RestrictionBlock";
import { RestrictionInfoBarProps } from "../components/RestrictionInfoBar";
import { TimeListBlockProps } from "../components/TimeListBlock/TimeListBlock";
import {
  AgendaTime,
  BucketCourseMap,
  DurationAgendaRowData,
  EventProps,
  RestrictionCourseOrBucket,
  SelectedRow,
} from "../types";
import { getCourseMessage } from "../utils/getCourseMessage";
import { useTimeListComponentsAgendaFragment_agenda$key } from "./__generated__/useTimeListComponentsAgendaFragment_agenda.graphql";

const getRateRange = (rates: (number | null)[]) => {
  if (rates.length === 0) {
    return [];
  }

  const nonNullRates = rates.flatMap((r) =>
    !isNil(r) && Number.isFinite(r) ? [r] : []
  );

  if (nonNullRates.length === 0) {
    return [];
  }

  const min = Math.min(...nonNullRates);
  const max = Math.max(...nonNullRates);

  return [min, max];
};

const getRestrictionColor = (
  visibilityColor?: string | null
): RestrictionBlockProps["visibilityColor"] => {
  switch (visibilityColor) {
    case "A":
    case "B":
    case "C":
    case "D":
    case "E":
    case "F":
      return visibilityColor;
    default:
      return null;
  }
};

const getTimeStringFromAgendaDateTime = (dateTime: string | null | undefined) =>
  dateTime ? format(convertAgendaDateTimeToDate(dateTime), "HH:mm:ss") : null;

const compareAgendaDateTimes = (
  dateTimeA: string | null | undefined,
  dateTimeB: string | null | undefined
) => {
  if (dateTimeA && dateTimeB) {
    return (
      convertAgendaDateTimeToDate(dateTimeA).getTime() -
      convertAgendaDateTimeToDate(dateTimeB).getTime()
    );
  }

  return 0;
};

const compareClockTimes = (timeA: string, timeB: string) =>
  compareAgendaTimes(timeA, timeB, "00:00:00");

const blockEndsBeforeFirstTimeSlot = (
  startAt: string | null | undefined,
  endAt: string,
  firstTimeSlot: string
) => {
  if (!startAt) {
    return false;
  }

  return (
    compareClockTimes(startAt, firstTimeSlot) < 0 &&
    compareClockTimes(endAt, firstTimeSlot) < 0 &&
    compareClockTimes(endAt, startAt) >= 0
  );
};

export const useTimeListComponents = (
  agendaFragmentRef: useTimeListComponentsAgendaFragment_agenda$key | null,
  courseIds: string[],
  bucketCourseMap: BucketCourseMap,
  onRowPress: (row: SelectedRow) => void,
  isMember: boolean = false
) => {
  const { currencySymbol } = useGetClubCurrency();
  const agenda = useFragment(
    useTimeListComponentsAgendaFragmentString,
    agendaFragmentRef
  );

  const { plural: copyOverridePlural } = useFacilityCourseNoun(
    agenda?.facility ?? null
  );

  const timeListData = useMemo(() => {
    const availableTimes = agenda?.availableTimes ?? [];

    const orderedAvailableTimes = availableTimes
      ? availableTimes
          .flatMap((at) => (at?.time ? [at] : []))
          .sort((a, b) => compareAgendaDateTimes(a.dateTime, b.dateTime))
      : [];

    const groupedByTime = availableTimes
      ? groupBy(orderedAvailableTimes, "time")
      : null;

    const sortedKeys = groupedByTime ? Object.keys(groupedByTime) : null;

    const firstTimeSlot = orderedAvailableTimes[0]?.time ?? "00:00:00";

    const facilityDurations = agenda?.facility?.durations || [];

    const minGroupSize = agenda?.facility?.minMaxGroupSize?.min || 1;
    const maxGroupSize = agenda?.facility?.minMaxGroupSize?.max || 10;
    // We never allow multiple bookings in duration -- so used capacity is not relevant.
    // Either the slot is fully available -- or it's not.
    const usedCapacity = 0;

    //Blocks
    const blocks: TimeListBlockProps[] = agenda?.blocks?.edges
      ? agenda?.blocks?.edges?.reduce((acc: TimeListBlockProps[], cur) => {
          if (cur && cur.node) {
            const endAt =
              (cur.node?.startAt &&
                format(
                  add(parse(cur.node.startAt, "HH:mm:ss", new Date()), {
                    minutes: cur.node.durationMins ?? 0,
                  }),
                  "HH:mm:ss"
                )) ||
              "23:59:59";
            if (
              !blockEndsBeforeFirstTimeSlot(
                cur.node?.startAt,
                endAt,
                firstTimeSlot
              ) &&
              compareAgendaTimes(endAt, firstTimeSlot, firstTimeSlot) >= 0 &&
              cur.node.courses?.edges?.[0]?.node?.id
            ) {
              const blockCourses: RestrictionCourseOrBucket[] = cur?.node
                ?.courses?.edges
                ? cur.node.courses.edges.map((courseEdge) => ({
                    id: courseEdge?.node?.id ?? "",
                    name: courseEdge?.node?.name ?? "",
                  }))
                : [];
              const blockProps: TimeListBlockProps = {
                color: cur.node.blockSeries.color,
                name: cur.node.blockSeries.name,
                durationMins: cur.node.durationMins ?? 0,
                startAt: cur.node.startAt,
                courseMessage: getCourseMessage(
                  blockCourses,
                  bucketCourseMap,
                  courseIds,
                  copyOverridePlural
                ),
              };
              acc.push(blockProps);
            }
          }

          return acc;
        }, [])
      : [];

    //Events
    const events: EventProps[] = agenda?.events?.edges
      ? agenda.events.edges.reduce((acc: EventProps[], cur) => {
          const matchingSessions = cur?.node?.eventSessions?.edges
            ? cur.node.eventSessions.edges.filter(
                (e) =>
                  e?.node?.agenda.date &&
                  e.node.agenda.date === agenda?.date &&
                  compareAgendaTimes(
                    e.node.timeSlots?.lastDateTime
                      ? getTimeStringFromAgendaDateTime(
                          e.node.timeSlots.lastDateTime
                        ) ?? "23:00:00"
                      : "23:00:00",
                    firstTimeSlot,
                    firstTimeSlot
                  ) >= 0
              )
            : undefined;
          if (matchingSessions?.length) {
            const newEvents = matchingSessions.reduce(
              (eventSessionAcc: EventProps[], eventSession) => {
                if (cur?.node && eventSession?.node) {
                  eventSessionAcc.push({
                    id: cur.node.id,
                    name: cur.node.eventSeries.name,
                    startAt: eventSession.node?.startAt ?? "",
                    endAt: eventSession.node?.endAt ?? "",
                    fragmentRef: eventSession.node,
                  });
                }
                return eventSessionAcc;
              },
              []
            );
            acc.push(...newEvents);
          }

          return acc;
        }, [])
      : [];

    // Restrictions
    const blockingRestrictions: RestrictionBlockProps[] = agenda
      ?.blockingRestrictions?.edges
      ? agenda.blockingRestrictions.edges.reduce(
          (acc: RestrictionBlockProps[], cur) => {
            const normalizedRule = normalizeBookingRule(cur?.node);

            if (normalizedRule?.visibility === "RESTRICTION_ONLY") {
              //only add the restriction if it's visible
              const blockCourses: RestrictionCourseOrBucket[] = normalizedRule
                ?.courses?.edges
                ? normalizedRule.courses.edges.map((courseEdge) => ({
                    id: courseEdge?.node?.id ?? "",
                    name: courseEdge?.node?.name ?? "",
                  }))
                : [];
              const props: RestrictionBlockProps = {
                id: normalizedRule.id,
                name: normalizedRule.name,
                displayName: normalizedRule.displayName,
                liftAt: cur?.liftAt ?? null,
                memberMessage: normalizedRule.memberMessage,
                allDay: normalizedRule.allDay,
                visibility: normalizedRule.visibility,
                visibilityColor: getRestrictionColor(
                  normalizedRule.visibilityColor
                ),
                courseMessage: getCourseMessage(
                  blockCourses,
                  bucketCourseMap,
                  courseIds,
                  copyOverridePlural
                ),
                startAt: normalizedRule.startAt ?? null,
                endAt: normalizedRule.endAt ?? null,
              };
              acc.push(props);
            }
            return acc;
          },
          []
        )
      : [];

    const infoRestrictions: RestrictionInfoBarProps[] = agenda?.infoRestrictions
      ?.edges
      ? agenda.infoRestrictions.edges.reduce(
          (acc: RestrictionInfoBarProps[], cur) => {
            const normalizedRule = normalizeBookingRule(cur?.node);

            if (
              normalizedRule?.id &&
              !blockingRestrictions.some((br) => br.id === normalizedRule.id)
            ) {
              const blockCourses: RestrictionCourseOrBucket[] = normalizedRule
                ?.courses?.edges
                ? normalizedRule.courses.edges.map((courseEdge) => ({
                    id: courseEdge?.node?.id ?? "",
                    name: courseEdge?.node?.name ?? "",
                  }))
                : [];
              const props: RestrictionInfoBarProps = {
                id: normalizedRule.id,
                name: normalizedRule.name,
                displayName: normalizedRule.displayName,
                memberMessage: normalizedRule.memberMessage,
                allDay: normalizedRule.allDay,
                visibilityColor: getRestrictionColor(
                  normalizedRule.visibilityColor
                ),
                courseMessage: getCourseMessage(
                  blockCourses,
                  bucketCourseMap,
                  courseIds,
                  copyOverridePlural
                ),
                startAt: normalizedRule.startAt ?? null,
                endAt: normalizedRule.endAt ?? null,
              };
              acc.push(props);
            }
            return acc;
          },
          []
        )
      : [];

    const agendaTimes: AgendaTime[] | null =
      availableTimes && sortedKeys && groupedByTime
        ? sortedKeys?.flatMap((key) => {
            const times = groupedByTime[key];
            const groupLength = times.length;

            return times.map((time, i) => {
              const variant =
                groupLength === 1
                  ? "only"
                  : i === 0
                  ? "first"
                  : i === groupLength - 1
                  ? "last"
                  : "middle";

              const availableMinutes = (time?.seriesLength || 1) * 15;
              const minDuration = facilityDurations[0] ?? 15;
              const maxDuration =
                [...facilityDurations]
                  .reverse()
                  .find((d) => d && d <= availableMinutes) ?? minDuration;

              // minPrice/maxPrice now come back as pre-multiplied booking totals from the server
              // (totalDurationBookingPrice), so the client just sums booking + player rates.
              const bookingRateRange = getRateRange(
                time?.bookingRates?.flatMap((r) =>
                  r
                    ? [
                        !isNil(r.minPrice) ? r.minPrice : null,
                        !isNil(r.maxPrice) ? r.maxPrice : null,
                      ]
                    : []
                ) || []
              );

              const capacity = time?.capacity ?? maxGroupSize;

              const playerRateRange = getRateRange(
                time?.playerRates?.flatMap((r) =>
                  r
                    ? [
                        !isNil(r.minPrice) ? r.minPrice : null,
                        !isNil(r.maxPrice) ? r.maxPrice : null,
                      ]
                    : []
                ) || []
              );

              const rateRange = zipWith(
                bookingRateRange,
                playerRateRange,
                (x, y) =>
                  `${currencySymbol}${ConvertCentsToDollars(
                    (x ?? 0) + (y ?? 0)
                  )}`
              );

              return {
                type: "AgendaTime",
                data: {
                  key: `${time?.courseBucket?.id}:${time?.time}`,
                  time: time?.time || "",
                  dateTime: time?.dateTime,
                  agendaDate: agenda?.date,
                  agendaId: agenda?.id || "",
                  courseBucketName: time?.courseBucket?.name,
                  courseBucketId: time?.courseBucket?.id,
                  variant: variant,
                  minDuration: minDuration,
                  maxDuration: maxDuration,
                  minGroupSize: minGroupSize,
                  maxGroupSize: maxGroupSize,
                  pricingPreview: isMember ? null : time.pricingPreview,
                  rateRange: isMember ? rateRange : undefined,
                  onPress: onRowPress,
                  capacity: capacity,
                  usedCapacity: usedCapacity,
                },
                startsAt: time?.time ?? null,
              };
            });
          })
        : null;

    // Then need to sort them into order
    const timeListData: DurationAgendaRowData[] = blocks
      .map(
        (b) =>
          ({
            type: "Block",
            startAt: b.startAt,
            durationMins: b.durationMins,
            data: b,
          } as DurationAgendaRowData)
      )
      .concat(
        events.map(
          (e) =>
            ({
              type: "Event",
              startAt: e.startAt,
              data: e.fragmentRef,
              key: e.id,
            } as DurationAgendaRowData)
        )
      )
      .concat(
        blockingRestrictions.map((r) => ({
          type: "BlockingRestriction",
          startAt: r.allDay ? firstTimeSlot : r.startAt ? r.startAt : "",
          data: r,
        }))
      )
      .concat(
        infoRestrictions
          .filter((r) => {
            //Filter out any restrictions we already have as blocking restrictions
            return (
              blockingRestrictions.findIndex((br) => br.id === r.id) === -1
            );
          })
          .map((r) => ({
            type: "InfoRestriction",
            startAt: r.allDay ? firstTimeSlot : r.startAt ? r.startAt : "",
            endAt: r.allDay ? "23:59:59" : r.endAt ? r.endAt : "",
            data: r,
          }))
      )
      .concat(
        (agendaTimes ?? []).map((at) => ({
          type: "AgendaTime",
          startAt: at.data.time,
          data: at.data,
        }))
      )
      .sort((a, b) => {
        if (!a.startAt || !b.startAt) {
          if (!!a.startAt) {
            return 1;
          }
          if (!!b.startAt) {
            return -1;
          }
          return 0;
        }
        const agendaTimeSort = compareAgendaDateTimes(
          a.type === "AgendaTime" ? a.data.dateTime : null,
          b.type === "AgendaTime" ? b.data.dateTime : null
        );

        if (agendaTimeSort !== 0) {
          return agendaTimeSort;
        }

        return compareAgendaTimes(a.startAt, b.startAt, firstTimeSlot);
      });

    if (!agendaTimes || agendaTimes.length < 1) {
      timeListData.push({
        type: "NoAvailableTimes",
        startAt: "23:59:59",
      });
    }

    return timeListData;
  }, [
    agenda?.availableTimes,
    agenda?.facility?.durations,
    agenda?.facility?.minMaxGroupSize?.min,
    agenda?.facility?.minMaxGroupSize?.max,
    agenda?.blocks?.edges,
    agenda?.events?.edges,
    agenda?.blockingRestrictions?.edges,
    agenda?.infoRestrictions?.edges,
    agenda?.date,
    agenda?.id,
    bucketCourseMap,
    courseIds,
    copyOverridePlural,
    onRowPress,
    isMember,
    currencySymbol,
  ]);

  return timeListData;
};

export const useTimeListComponentsAgendaFragmentString = graphql`
  fragment useTimeListComponentsAgendaFragment_agenda on Agenda {
    id
    date
    facility {
      ...useFacilityCourseNoun_facility
      durations
      minMaxGroupSize(bookingType: STANDARD) {
        min
        max
      }
    }
    availableTimes(
      minDuration: $minDuration
      maxDuration: $maxDuration
      playerCount: $groupSize
      courseBucketIds: $courseBucketIds
      timeGte: $startTime
      timeLte: $endTime
    ) {
      time
      dateTime
      capacity(bookingType: STANDARD)
      courseBucket {
        id
        name
      }
      seriesLength
      bookingRates: rates(ratePeriodType: BOOKING) @include(if: $isMember) {
        minPrice: totalDurationBookingPrice(
          duration: $minDuration
          playerCount: $minGroupSize
        )
        maxPrice: totalDurationBookingPrice(duration: $maxDuration)
      }
      playerRates: rates(ratePeriodType: PLAYER) @include(if: $isMember) {
        minPrice: totalDurationBookingPrice(
          duration: $minDuration
          playerCount: $minGroupSize
        )
        maxPrice: totalDurationBookingPrice(duration: $maxDuration)
      }
      pricingPreview @skip(if: $isMember) {
        mode
        amount
      }
    }
    blockingRestrictions: bookingRules(
      type: [RESTRICTION]
      bookingType: [STANDARD]
      isBlocking: true
      memberVisibility: [RESTRICTION_ONLY, SLOTS_AND_RESTRICTION]
    ) {
      edges {
        liftAt
        node {
          id
          name
          displayName
          ... on BookingRestrictionRule {
            memberMessage
            visibilityColor
            memberVisibility
            liftBookingRuleWindow {
              daysAhead
              time
            }
            slotCriteria {
              allDay
              startAt
              endAt
              courses {
                count
                edges {
                  node {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
    infoRestrictions: bookingRules(
      type: [RESTRICTION]
      isBlocking: false
      memberVisibility: [RESTRICTION_ONLY, SLOTS_AND_RESTRICTION]
    ) {
      edges {
        node {
          id
          name
          displayName
          ... on BookingRestrictionRule {
            memberMessage
            visibilityColor
            memberVisibility
            slotCriteria {
              allDay
              startAt
              endAt
              courses {
                count
                edges {
                  node {
                    name
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
    blocks(first: 1000, isVisibleToMembers: true) {
      edges {
        node {
          id
          startAt
          durationMins
          blockSeries {
            name
            color
          }
          courses {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
    events(first: 1000) {
      edges {
        node {
          id
          eventSeries {
            name
          }
          eventSessions(first: 1000) {
            edges {
              node {
                startAt
                endAt
                agenda {
                  date
                }
                timeSlots {
                  lastDateTime
                }
                ...TimeListEventsCard_EventSession
              }
            }
          }
        }
      }
    }
  }
`;
